import { validateConfig, type PiMentisConfig } from "@pi-mentis/pi-mentis-core";

export type SystemSettingValue = string | number | boolean;

export interface SystemSettingField {
  readonly id: string;
  readonly label: string;
  readonly path: readonly string[];
  readonly value: SystemSettingValue;
  readonly choices?: readonly string[];
  readonly requiresConfirmation?: boolean;
}

export interface SystemSettingGroup {
  readonly id: string;
  readonly title: string;
  readonly fields: readonly SystemSettingField[];
}

export const PROTECTED_SYSTEM_SETTING_PATHS = new Set([
  "runtime.piVersion",
  "inference.provider",
  "inference.siliconflow.baseUrl",
  "inference.siliconflow.apiKeyEnv",
  "inference.siliconflow.embedding.model",
  "inference.siliconflow.embedding.dimensions",
  "inference.siliconflow.rerank.model",
  "inference.siliconflow.rerank.returnDocuments",
  "inference.openrouter.baseUrl",
  "inference.openrouter.apiKeyEnv",
  "inference.openrouter.embedding.model",
  "inference.openrouter.embedding.dimensions",
  "inference.embedding.required",
  "intelligence.temporal.enabled",
]);

const SETTING_CHOICES: Readonly<Record<string, readonly string[]>> = {
  "inference.siliconflow.embedding.encodingFormat": ["float", "base64"],
  "inference.siliconflow.embedding.truncate": ["reject", "left", "right"],
  "inference.openrouter.embedding.encodingFormat": ["float", "base64"],
  "inference.openrouter.embedding.truncate": ["reject"],
  "observability.logLevel": ["silent", "error", "warn", "info", "debug"],
};

interface GroupRoute {
  readonly id: string;
  readonly title: string;
  readonly labelOffset: number;
}

const GROUP_ORDER = [
  "knowledge",
  "memory",
  "retrieval",
  "inference",
  "storage",
  "performance",
  "observability",
  "working-memory",
  "memory-formation",
  "consolidation",
  "context-learning",
] as const;

function route(path: readonly string[]): GroupRoute | undefined {
  if (path[0] === "knowledge") return { id: "knowledge", title: "Knowledge", labelOffset: 1 };
  if (path[0] === "memory") return { id: "memory", title: "Memory", labelOffset: 1 };
  if (path[0] === "retrieval") return { id: "retrieval", title: "Retrieval", labelOffset: 1 };
  if (path[0] === "inference") return { id: "inference", title: "Inference", labelOffset: 1 };
  if (path[0] === "storage") return { id: "storage", title: "Storage", labelOffset: 1 };
  if (path[0] === "performance") return { id: "performance", title: "Performance", labelOffset: 1 };
  if (path[0] === "observability")
    return { id: "observability", title: "Observability", labelOffset: 1 };
  if (path[0] !== "intelligence") return undefined;
  if (path[1] === "workingMemory")
    return { id: "working-memory", title: "Working Memory", labelOffset: 2 };
  if (path[1] === "memoryFormation")
    return { id: "memory-formation", title: "Memory Formation", labelOffset: 2 };
  if (path[1] === "consolidation")
    return { id: "consolidation", title: "Consolidation", labelOffset: 2 };
  return { id: "context-learning", title: "Context & Learning", labelOffset: 1 };
}

function words(segment: string): string {
  return segment
    .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .replace(/\bCpu\b/gu, "CPU")
    .replace(/\bIo\b/gu, "I/O")
    .replace(/\bTtl\b/gu, "TTL")
    .replace(/\bUrl\b/gu, "URL")
    .replace(/\bMs\b/gu, "(ms)")
    .replace(/\bBytes\b/gu, "(bytes)")
    .replace(/^./u, (character) => character.toUpperCase());
}

function label(path: readonly string[], offset: number): string {
  return path.slice(offset).map(words).join(" / ");
}

function leaves(
  value: unknown,
  path: readonly string[] = [],
): Array<{
  readonly path: readonly string[];
  readonly value: SystemSettingValue;
}> {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return [{ path, value }];
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => leaves(child, [...path, key]));
}

export function systemSettingGroups(config: PiMentisConfig): readonly SystemSettingGroup[] {
  const grouped = new Map<string, { route: GroupRoute; fields: SystemSettingField[] }>();
  for (const leaf of leaves(config)) {
    const id = leaf.path.join(".");
    if (PROTECTED_SYSTEM_SETTING_PATHS.has(id)) continue;
    const destination = route(leaf.path);
    if (destination === undefined) continue;
    const entry = grouped.get(destination.id) ?? { route: destination, fields: [] };
    entry.fields.push({
      id,
      label: label(leaf.path, destination.labelOffset),
      path: leaf.path,
      value: leaf.value,
      ...(SETTING_CHOICES[id] === undefined ? {} : { choices: SETTING_CHOICES[id] }),
      ...(id === "storage.rootDir" ? { requiresConfirmation: true } : {}),
    });
    grouped.set(destination.id, entry);
  }
  return GROUP_ORDER.flatMap((id) => {
    const entry = grouped.get(id);
    return entry === undefined
      ? []
      : [{ id, title: entry.route.title, fields: entry.fields } satisfies SystemSettingGroup];
  });
}

export function formatSystemSettingValue(value: SystemSettingValue): string {
  if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
  const rendered = String(value);
  return rendered.length <= 44 ? rendered : `${rendered.slice(0, 41)}…`;
}

export function systemSettingOption(field: SystemSettingField): string {
  return `${field.label} — ${formatSystemSettingValue(field.value)}`;
}

export function parseSystemSettingInput(
  field: SystemSettingField,
  input: string,
): SystemSettingValue {
  const trimmed = input.trim();
  if (typeof field.value === "string") {
    if (trimmed === "") throw new Error(`${field.label} cannot be empty`);
    if (field.choices !== undefined && !field.choices.includes(trimmed)) {
      throw new Error(`${field.label} must be one of: ${field.choices.join(", ")}`);
    }
    return trimmed;
  }
  if (typeof field.value === "boolean") {
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    throw new Error(`${field.label} must be enabled or disabled`);
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) throw new Error(`${field.label} must be a finite number`);
  if (Number.isInteger(field.value) && !Number.isSafeInteger(parsed)) {
    throw new Error(`${field.label} must be an integer`);
  }
  return parsed;
}

function mutableRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Mentis setting path does not reference an object");
  }
  return value as Record<string, unknown>;
}

export function applySystemSetting(
  config: PiMentisConfig,
  field: Pick<SystemSettingField, "path">,
  value: SystemSettingValue,
): PiMentisConfig {
  const next = structuredClone(config);
  let parent = mutableRecord(next);
  for (const segment of field.path.slice(0, -1)) parent = mutableRecord(parent[segment]);
  const key = field.path.at(-1);
  if (key === undefined) throw new Error("Mentis setting path is empty");
  parent[key] = value;
  return validateConfig(next);
}
